import { FC, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import cls from 'clsx';
import { observer } from 'mobx-react-lite';
import { BaseAvailabilityStatus, CurrencyCurrency } from '@linktivity/ars-neo';
import { Close as CloseIcon } from '@linktivity/link-icons';
import { Drawer, Button } from '@linktivity/link-ui';
import { Price, Confirm } from '@link/share/components';
import { useIsMobile } from '@link/share/hooks';
import CartIcon from '@link/share/icons/cart.svg?react';
import EmptyIcon from '@link/share/icons/empty.svg?react';
import { buildLocalizedPath } from '@link/share/utils';
import CartItem from './CartItem';
import { useHeaderContext } from './context';
import { CartStore } from './header.types';
import stylesCart from './cart.module.css';
import styles from './header.module.css';

type DrawerPosition = 'bottom' | 'right';

interface CartProps {
  cartStore: CartStore;
  loggedIn: boolean;
  currentCurrency?: CurrencyCurrency;
}

const Cart: FC<CartProps> = observer(
  ({ cartStore, loggedIn, currentCurrency }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [visible, setVisible] = useState(false);
    const sessions = cartStore.sessions;
    const availabilityInfos = cartStore.availabilityInfos;
    const isMobile = useIsMobile();
    const position: DrawerPosition = useMemo(
      () => (isMobile ? 'bottom' : 'right'),
      [isMobile]
    );
    const sessionsWithAvailability = useMemo(() => {
      const availabilityStatusMap = new Map<string, BaseAvailabilityStatus>();
      (availabilityInfos ?? []).forEach(info => {
        if (info.sessionId && info.availabilityStatus) {
          availabilityStatusMap.set(info.sessionId, info.availabilityStatus);
        }
      });

      return [...sessions]
        .map(session => {
          const availabilityStatus = session.id
            ? availabilityStatusMap.get(session.id)
            : undefined;
          const isDisabled =
            availabilityStatus !== undefined &&
            availabilityStatus !== BaseAvailabilityStatus.AvailabilityStatusOk;

          return {
            session,
            isDisabled
          };
        })
        .sort((sessionA, sessionB) => {
          if (sessionA.isDisabled === sessionB.isDisabled) {
            return 0;
          }

          return sessionA.isDisabled ? 1 : -1;
        });
    }, [sessions, availabilityInfos]);

    const totalAmount = useMemo(() => {
      let total = 0;
      for (const session of sessions) {
        const { form, optionForms } = session;
        total += (form?.activityForm?.planUnitItems ?? []).reduce(
          (sum, unit) => sum + (unit.amount ?? 0) * (unit.count ?? 0),
          0
        );
        total += (optionForms ?? []).reduce(
          (sum, option) =>
            sum +
            (option.activityForm?.planUnitItems ?? []).reduce(
              (unitSum, unit) =>
                unitSum + (unit.amount ?? 0) * (unit.count ?? 0),
              0
            ),
          0
        );
      }
      return total;
    }, [sessions]);

    const { closeMobileMenu } = useHeaderContext();

    const handleCartClick = useCallback(() => {
      if (!loggedIn) {
        closeMobileMenu?.();
        navigate(buildLocalizedPath('login'));
        return;
      }
      cartStore.open();
    }, [loggedIn, navigate, cartStore, closeMobileMenu]);

    const submitHandler = useCallback(async () => {
      const status = await cartStore.checkCartAvailability();
      if (status === BaseAvailabilityStatus.AvailabilityStatusOk) {
        if (!sessions.length) {
          return;
        }
        cartStore.close();
        navigate(buildLocalizedPath(`book/confirm?cartId=${cartStore.cartId}`));
      } else {
        setVisible(true);
      }
    }, [cartStore, navigate, sessions]);

    const deleteHandler = useCallback(async () => {
      const unavailableIds = Array.from(
        (cartStore.availabilityInfos ?? []).reduce<Set<string>>((ids, info) => {
          if (
            info.availabilityStatus !==
              BaseAvailabilityStatus.AvailabilityStatusOk &&
            info.sessionId
          ) {
            ids.add(info.sessionId);
          }
          return ids;
        }, new Set())
      );
      if (unavailableIds.length === 0) {
        return;
      }

      await cartStore.removeItem(unavailableIds);

      if (!cartStore.sessions.length) {
        return;
      }
      cartStore.close();
      navigate(buildLocalizedPath(`book/confirm?cartId=${cartStore.cartId}`));
    }, [cartStore, navigate]);

    return (
      <>
        <button
          className={styles.navButton}
          type="button"
          onClick={handleCartClick}
        >
          <CartIcon className={styles.navIcon} />
          <span className={styles.text}>{t('share.cart.button')}</span>
          {sessions.length > 0 && (
            <span className={stylesCart.navSum}>{sessions.length}</span>
          )}
        </button>
        <Drawer
          visible={cartStore.visible}
          position={position}
          onClose={() => {
            cartStore.close();
            closeMobileMenu?.();
          }}
        >
          <div
            className={cls(
              stylesCart.drawerContent,
              stylesCart[position as DrawerPosition]
            )}
          >
            <div className={stylesCart.drawerHeader}>
              <p className={stylesCart.drawerTitle}>{t('share.cart.button')}</p>
              <button
                className={stylesCart.drawerClose}
                type="button"
                onClick={() => cartStore.close()}
              >
                <CloseIcon />
              </button>
            </div>
            <div className={stylesCart.cart}>
              {sessions.length > 0 ? (
                <>
                  <div className={stylesCart.cartItemsContainer}>
                    <div className={stylesCart.cartItems}>
                      {sessionsWithAvailability.map(
                        ({ session, isDisabled }) => {
                          const { id: sessionId, form, optionForms } = session;
                          const activityForm = form?.activityForm;

                          if (!activityForm || !sessionId) {
                            return null;
                          }

                          return (
                            <div
                              className={cls(stylesCart.cartItem, {
                                [stylesCart.cartItemDisabled]: isDisabled
                              })}
                              key={sessionId}
                            >
                              <CartItem
                                item={{
                                  id: sessionId,
                                  session: {
                                    ...session,
                                    form
                                  }
                                }}
                                disabled={isDisabled}
                                currentCurrency={currentCurrency}
                                onRemove={id => cartStore.removeItem([id])}
                              />
                              {(optionForms ?? []).map(option => (
                                <CartItem
                                  key={`${sessionId}_${option.activityForm?.activityId}`}
                                  item={{
                                    id: option.activityForm?.activityId ?? '',
                                    session: {
                                      ...session,
                                      form: option
                                    }
                                  }}
                                  disabled={isDisabled}
                                  className={stylesCart.cartItemOption}
                                />
                              ))}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                  <div className={stylesCart.cartFooter}>
                    <div className={stylesCart.total}>
                      <span className={stylesCart.totalCount}>
                        {t('share.cart.totalCount', {
                          count: sessions.length
                        })}
                      </span>
                      <span className={stylesCart.totalAmount}>
                        {t('share.cart.totalAmount')}
                        <Price
                          amount={totalAmount}
                          currencyCode={currentCurrency}
                        />
                      </span>
                    </div>
                    <Button
                      className={stylesCart.cartFooterButton}
                      onClick={submitHandler}
                    >
                      {t('share.payment.submit')}
                    </Button>
                    <Confirm
                      visible={visible}
                      title={t('share.cart.availabilityConfirmTitle')}
                      content={t('share.cart.availabilityConfirmContent')}
                      confirmText={t('share.cart.availabilityConfirmProceed')}
                      cancelText={t('share.cart.availabilityConfirmBack')}
                      onClose={() => setVisible(false)}
                      onConfirm={deleteHandler}
                    />
                  </div>
                </>
              ) : (
                <div className={stylesCart.empty}>
                  <EmptyIcon className={stylesCart.emptyIcon} />
                  {t('share.cart.empty')}
                </div>
              )}
            </div>
          </div>
        </Drawer>
      </>
    );
  }
);

export default Cart;
